import { useEffect, useState } from 'react';
import { DurationSelector, type DurationSelectorProps } from '../../inputs/DurationSelector';
import { useEditorContext } from '../EditorContext';
import { ModalEditorItemBase } from './ModalEditorItemBase';

export interface ModalDurationItemProps<TFormType>
    extends Omit<DurationSelectorProps, 'defaultValue' | 'onChange' | 'onValidityChange'> {
    fieldKey: keyof TFormType;
    required?: boolean;
    label?: string;
    labelWidth?: number;
}

export const ModalDurationItem = <TFormType,>({
    label,
    labelWidth,
    fieldKey,
    required,
    ...durationSelectorProps
}: ModalDurationItemProps<TFormType>) => {
    const { form, loading, onSave } = useEditorContext<TFormType>();

    const fieldKeyString = fieldKey as string;
    const formError = form.errors[fieldKeyString];
    const [segmentError, setSegmentError] = useState<string | null>(null);

    const handleValidityChange = (error: string | null) => {
        setSegmentError(error);
        if (error) {
            form.setFieldError(fieldKeyString, error);
        } else {
            form.clearFieldError(fieldKeyString);
        }
    };

    // Re-apply our segment error if the form clears it (e.g. via the blur-time
    // validator running with no matching rule for this field). This keeps the
    // error visible after the input loses focus.
    useEffect(() => {
        if (segmentError && formError !== segmentError) {
            form.setFieldError(fieldKeyString, segmentError);
        }
    }, [segmentError, formError, form, fieldKeyString]);

    return (
        <ModalEditorItemBase
            label={label}
            labelWidth={labelWidth}
            error={formError}
            required={required}
            submitChanges={onSave}
        >
            <DurationSelector
                key={form.key(fieldKeyString)}
                {...form.getInputProps(fieldKeyString)}
                onValidityChange={handleValidityChange}
                disabled={loading}
                required={required}
                error={false}
                {...durationSelectorProps}
            />
        </ModalEditorItemBase>
    );
};
